/**
 * Las fotos de una oferta: una rejilla de miniaturas iguales, numeradas, que
 * se reordenan arrastrando.
 *
 * Una sola pieza para los asistentes de ofertas (digital, servicio, físico) y
 * de reservas: la galería es la misma columna (`offers.images`) y se ve igual
 * en la página pública —portada grande y tira de miniaturas que la cambia—, así
 * que se edita igual en todos lados.
 *
 * La primera es la portada (lleva su etiqueta): la que sale en la lista, en las
 * tarjetas del catálogo y en la página de pago de Stripe. Para cambiarla se
 * arrastra otra al primer sitio —o con sus flechas, que sirven en el teléfono y
 * con el teclado—. El número de cada una es su posición.
 *
 * Añadir abre el selector en modo varias: se marcan todas las que se quieran y
 * entran juntas, en el orden en que se marcaron.
 *
 * `PreviewGallery`, al final, es su reflejo en la maqueta de los asistentes.
 */
import { useState } from 'react'
import { IconChevronLeft, IconChevronRight, IconGripVertical, IconPhotoPlus, IconPlus, IconTrash } from '@tabler/icons-react'
import { ImageLibraryPickerDialog } from '../../ImageLibraryPickerDialog'
import { useI18n } from '../../../lib/i18n'

/** El mismo tope que pone el servidor (`offerInput.images`). */
export const MAX_GALLERY_IMAGES = 8

/** La lista con el elemento de `from` movido a `to`. */
function moved(list: string[], from: number, to: number): string[] {
  const next = [...list]
  const [item] = next.splice(from, 1)
  if (item !== undefined) next.splice(to, 0, item)
  return next
}

export function OfferGalleryField({ token, projectId, images, onChange, onFocusMedia }: {
  token: string
  projectId: string
  images: string[]
  onChange: (images: string[]) => void
  /** Para que la cámara de la maqueta mire a la portada mientras se elige. */
  onFocusMedia?: (focused: boolean) => void
}) {
  const { t } = useI18n()
  const [picking, setPicking] = useState(false)
  /* Qué se arrastra y sobre qué casilla está, para pintar el hueco. */
  const [dragFrom, setDragFrom] = useState<number | null>(null)
  const [dragOver, setDragOver] = useState<number | null>(null)
  const full = images.length >= MAX_GALLERY_IMAGES

  const remove = (url: string) => onChange(images.filter((row) => row !== url))
  const move = (from: number, to: number) => {
    if (to < 0 || to >= images.length || from === to) return
    onChange(moved(images, from, to))
  }

  const picker = picking ? (
    <ImageLibraryPickerDialog
      token={token}
      projectId={projectId}
      multiple={{
        max: MAX_GALLERY_IMAGES - images.length,
        onSelectMany: (urls) => onChange([...images, ...urls.filter((url) => !images.includes(url))].slice(0, MAX_GALLERY_IMAGES)),
      }}
      onClose={() => setPicking(false)}
    />
  ) : null

  if (!images.length) {
    return (
      <div className="dash-gallery-field">
        <button
          type="button"
          className="dash-svc-image-drop"
          onFocus={() => onFocusMedia?.(true)}
          onBlur={() => onFocusMedia?.(false)}
          onClick={() => setPicking(true)}
        >
          <IconPhotoPlus size={18} />
          <span><strong>{t('offers.new.uploadTitle')}</strong><small>{t('gallery.emptyHint', { max: MAX_GALLERY_IMAGES })}</small></span>
        </button>
        {picker}
      </div>
    )
  }

  return (
    <div className="dash-gallery-field">
      <ul className="dash-gallery-grid">
        {images.map((url, index) => (
          <li
            key={url}
            className={`dash-gallery-tile${dragFrom === index ? ' is-dragging' : ''}${dragOver === index && dragFrom !== index ? ' is-over' : ''}`}
            draggable
            onDragStart={(event) => { setDragFrom(index); event.dataTransfer.effectAllowed = 'move' }}
            onDragOver={(event) => { event.preventDefault(); if (dragOver !== index) setDragOver(index) }}
            onDragLeave={() => setDragOver((current) => (current === index ? null : current))}
            onDrop={(event) => { event.preventDefault(); if (dragFrom !== null) move(dragFrom, index); setDragFrom(null); setDragOver(null) }}
            onDragEnd={() => { setDragFrom(null); setDragOver(null) }}
          >
            <img src={url} alt="" loading="lazy" draggable={false} />
            <span className="dash-gallery-num" aria-hidden="true">{index + 1}</span>
            {index === 0 ? <span className="dash-gallery-badge">{t('gallery.cover')}</span> : null}
            <span className="dash-gallery-grip" aria-hidden="true"><IconGripVertical size={13} /></span>
            <span className="dash-gallery-tile-actions">
              <button type="button" disabled={index === 0} aria-label={t('gallery.moveLeft')} data-tooltip={t('gallery.moveLeft')} onClick={() => move(index, index - 1)}>
                <IconChevronLeft size={13} />
              </button>
              <button type="button" disabled={index === images.length - 1} aria-label={t('gallery.moveRight')} data-tooltip={t('gallery.moveRight')} onClick={() => move(index, index + 1)}>
                <IconChevronRight size={13} />
              </button>
              <button type="button" aria-label={t('offers.detail.removeImageAria')} data-tooltip={t('offers.detail.removeImage')} onClick={() => remove(url)}>
                <IconTrash size={13} />
              </button>
            </span>
          </li>
        ))}
        {full ? null : (
          <li className="dash-gallery-tile is-add">
            <button type="button" className="dash-gallery-add" aria-label={t('gallery.add')} onClick={() => setPicking(true)}>
              <IconPlus size={18} />
              <span>{t('gallery.add')}</span>
            </button>
          </li>
        )}
      </ul>
      <small className="dash-gallery-hint">{t('gallery.hint', { count: images.length, max: MAX_GALLERY_IMAGES })}</small>
      {picker}
    </div>
  )
}

/**
 * La galería en la maqueta: la portada y, si hay más, la tira de miniaturas
 * debajo, como sale en la página pública. Pulsar una la pone delante.
 */
export function PreviewGallery({ images, className = 'dash-svc-page-image' }: { images: string[]; className?: string }) {
  const [active, setActive] = useState(0)
  const shown = images[active] ?? images[0]
  if (!shown) return null
  return (
    <span className="dash-preview-gallery" data-zone="media">
      <img className={className} src={shown} alt="" />
      {images.length > 1 ? (
        <span className="dash-preview-gallery-thumbs">
          {images.map((url, index) => (
            <button key={url} type="button" className={url === shown ? 'is-on' : ''} onClick={() => setActive(index)} aria-label={String(index + 1)}>
              <img src={url} alt="" />
            </button>
          ))}
        </span>
      ) : null}
    </span>
  )
}
